import { useCallback, useMemo, useState } from "react";
import Papa from "papaparse";
import useDownloader from "react-use-downloader";

import useMutate from "../../../../../../hooks/useMutate";
import {
  downloadInvitationCsvTemplate,
  inviteUsersByCSVFile,
} from "../../../../../../services/apiUser";
import {
  validateInvitationCsvHeaders,
  validateInvitationCsvRows,
} from "../utils/uploadUserCSVUtils";

export function useUploadUserCSV({ onClose }) {
  const [openImportUsers, setOpenImportUsers] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [previewRows, setPreviewRows] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const { download } = useDownloader();

  const resetState = useCallback(() => {
    setSelectedFile(null);
    setErrorMessage("");
    setPreviewRows([]);
    setRawRows([]);
    setOpenImportUsers(false);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose?.();
  }, [onClose, resetState]);

  const handleUploadSuccess = useCallback(() => {
    handleClose();
  }, [handleClose]);

  const { mutate: submitFileMutate, isPending: isSubmittingFile } = useMutate(
    inviteUsersByCSVFile,
    ["LDAPinternalList", "users"],
    () => {},
    handleUploadSuccess
  );

  const parseCSVFile = useCallback((file) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete(results) {
        if (!results?.data?.length) {
          setErrorMessage("The CSV file is empty or contains no valid data.");
          setPreviewRows([]);
          setRawRows([]);
          return;
        }

        const headerValidationError = validateInvitationCsvHeaders(
          results.meta?.fields || []
        );

        if (headerValidationError) {
          setErrorMessage(headerValidationError);
          setPreviewRows([]);
          setRawRows([]);
          return;
        }

        const validatedResult = validateInvitationCsvRows(results.data);

        setErrorMessage("");
        setSelectedFile(file);
        setRawRows(results.data);
        setPreviewRows(validatedResult.rows);
        setOpenImportUsers(true);
      },
      error() {
        setErrorMessage("Unable to read the CSV file.");
        setPreviewRows([]);
        setRawRows([]);
      },
    });
  }, []);

  const handleFiles = useCallback(
    (files) => {
      const file = files?.[0];

      if (!file) {
        setErrorMessage("Please upload a CSV file.");
        return;
      }

      parseCSVFile(file);
    },
    [parseCSVFile]
  );

  const handleTemplateDownload = useCallback(() => {
    downloadInvitationCsvTemplate()
      .then((response) => {
        const blob = response?.data;
        const url = URL.createObjectURL(blob);
        download(url, "invitation_users_template.csv");
      })
      .catch(() => {
        setErrorMessage("Unable to download invitation template.");
      });
  }, [download]);

  const submitFileUpload = useCallback(() => {
    if (!selectedFile || !previewRows.length) {
      setErrorMessage("Please upload a valid CSV file.");
      return;
    }

    const { isValid } = validateInvitationCsvRows(rawRows);

    if (!isValid) {
      setErrorMessage("Please fix the invalid rows before submitting.");
      return;
    }

    setErrorMessage("");
    submitFileMutate({ file: selectedFile });
  }, [previewRows.length, rawRows, selectedFile, submitFileMutate]);

  const hasInvalidRows = useMemo(
    () => previewRows.some((row) => !row.isValid),
    [previewRows]
  );

  return {
    errorMessage,
    handleClose,
    handleFiles,
    handleTemplateDownload,
    hasInvalidRows,
    isSubmitting: isSubmittingFile,
    openImportUsers,
    previewRows,
    selectedFile,
    setOpenImportUsers,
    submitFileUpload,
  };
}
